/**
 * Configuration for EC Listing VAT Number Import feature.
 * Countries listed here will show the VAT Number Import section
 * in the EC Listing Open Returns Details page.
 */

import { FeatureFlagEnum } from './enums';

/**
 * Countries that support EC Listing VAT Number import functionality.
 * Add country codes to this array to enable the feature for those countries.
 */
export const EC_LISTING_IMPORT_SUPPORTED_COUNTRIES = ['BE', 'NL', 'LU'];

/**
 * EC Type mapping from template values to display labels and internal values.
 * Template uses numeric values (1-7) that map to specific EC types.
 * The value field should be the numeric string that the API expects.
 */
export const EC_TYPE_TEMPLATE_MAPPING = {
  1: { label: 'EC Goods', value: '1' },
  2: { label: 'Triangulation', value: '2' },
  3: { label: 'EC Services - art. 196', value: '3' },
  4: { label: 'Services - art. 194', value: '4' },
  5: { label: 'Transfers', value: '5' },
  6: { label: 'Transfers after exempted import', value: '6' },
  7: { label: 'EC goods after exempted import', value: '7' }
};

/**
 * Maximum file size for upload in MB
 */
export const MAX_UPLOAD_FILE_SIZE_MB = 2;

/**
 * Allowed file extensions for upload
 * Note: Both .xlsx and .xls are supported for compatibility
 */
export const ALLOWED_FILE_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Template filename for download
 */
export const EC_IMPORT_TEMPLATE_FILENAME = 'ec-listing-import-template.xls';

/**
 * Maps template EC type number to internal numeric value
 * 
 * @param {number|string} ecTypeNumber - The EC type number from template (1-7)
 * @returns {string} - The internal EC type numeric value (defaults to '1')
 */
export const mapECTypeFromTemplate = (ecTypeNumber) => {
  return EC_TYPE_TEMPLATE_MAPPING[parseInt(ecTypeNumber, 10)]?.value || '1';
};

/**
 * Checks if EC Listing VAT Number Import is enabled for a specific country.
 * 
 * @param {string} countryCode - The country code (e.g., 'BE', 'NL')
 * @param {object} featureFlags - The feature flags object from Redux state
 * @returns {boolean} True if feature flag is enabled AND country is supported
 */
export const isECListingImportEnabled = (countryCode, featureFlags) => {
  return featureFlags?.[FeatureFlagEnum.ENABLE_EC_LISTING_IMPORT] === true &&
         EC_LISTING_IMPORT_SUPPORTED_COUNTRIES.includes(countryCode?.toUpperCase());
};

export default {
  EC_LISTING_IMPORT_SUPPORTED_COUNTRIES,
  EC_TYPE_TEMPLATE_MAPPING,
  MAX_UPLOAD_FILE_SIZE_MB,
  ALLOWED_FILE_EXTENSIONS,
  EC_IMPORT_TEMPLATE_FILENAME,
  mapECTypeFromTemplate,
  isECListingImportEnabled
};

